import React, { useMemo, useRef, useCallback } from "react";
import { Line } from "react-chartjs-2";
import { ChartOptions } from "chart.js";
import styled from "styled-components";
import { format } from "date-fns";
import { theme } from "styles/theme";
import { useChartUi } from "./chartColors";
import { clampChartXToPresent, refreshNoFutureLimit, zoomLimitsNoFuture } from "./clampChartXToPresent";

const ChartWrapper = styled.div`
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0.5rem 0 1rem;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0;
  border-bottom: none;
`;

const ChartTitle = styled.h3`
  margin: 0;
  color: ${theme.colors.tertiary};
  font-size: 1.1rem;
  font-weight: 600;
`;

const ChartContainer = styled.div`
  height: 300px;
  position: relative;
  padding: 1rem 0;
  overflow: visible;
  touch-action: none;
`;

const ResetZoomButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: ${theme.colors.primaryTransparent};
  border: 1px solid ${theme.colors.tertiary};
  color: ${theme.colors.tertiary};
  padding: 0.25rem 0.5rem;
  border-radius: ${theme.borderRadius.small};
  font-size: 0.7rem;
  cursor: pointer;
  z-index: 5;

  &:hover {
    background: ${theme.colors.tertiaryTransparent};
  }
`;

interface EnhancedLineChartProps {
  title: string;
  processedData: Array<{
    timestamp: number;
    pH: number | null;
    ORP: number | null;
    Temperature: number | null;
    Salinity: number | null;
    Flow: number | null;
    Vsdw: number | null;
  }>;
  dataType: "pH" | "ORP" | "Temperature" | "Salinity" | "Flow" | "Vsdw";
  color: string;
  unit?: string;
  range?: {
    min?: number;
    max?: number;
  };
  customYAxis?: {
    ticks?: {
      callback?: (
        value: any,
        index: number,
        values: any[],
      ) => string | number | null | undefined;
    };
    min?: number;
    max?: number;
  };
  timeRangeLabel?: string; // Para identificar o tipo de período
  fromTimestamp?: number; // Para mostrar gráfico vazio com datas corretas
  toTimestamp?: number; // Para mostrar gráfico vazio com datas corretas
  zoomKey?: number; // Increment to trigger zoom reset
  onResetZoom?: () => void; // Callback for reset zoom button
  onViewportChange?: (minTimestamp: number) => void; // Fires on pan/zoom with new x min
}

export const EnhancedLineChart: React.FC<EnhancedLineChartProps> = ({
  title,
  processedData,
  dataType,
  color,
  unit = "",
  range = {},
  customYAxis,
  timeRangeLabel,
  fromTimestamp,
  toTimestamp,
  zoomKey,
  onResetZoom,
  onViewportChange,
}) => {
  const chartRef = useRef<any>(null);
  const chartUi = useChartUi();
  const [isZoomed, setIsZoomed] = React.useState(false);
  const isZoomedRef = useRef(isZoomed);
  isZoomedRef.current = isZoomed;
  // resetZoom() can fire zoom callbacks — ignore those so NOW stays disabled
  const suppressViewportRef = useRef(false);
  const isFirstZoomKeyRef = useRef(true);

  // Stable refs — declared early so all effects/memos can use them safely.
  const fromTimestampRef = useRef(fromTimestamp);
  fromTimestampRef.current = fromTimestamp;
  const toTimestampRef = useRef(toTimestamp);
  toTimestampRef.current = toTimestamp;

  const runProgrammaticZoomReset = useCallback(() => {
    if (!chartRef.current) return;
    suppressViewportRef.current = true;
    const chart = chartRef.current;
    chart.options.scales!.x!.min = fromTimestampRef.current;
    chart.options.scales!.x!.max = toTimestampRef.current;
    chart.resetZoom();
    setIsZoomed(false);
    requestAnimationFrame(() => {
      suppressViewportRef.current = false;
    });
  }, []);

  // Reset zoom when zoomKey changes (skip mount — avoids false NOW enable)
  React.useEffect(() => {
    if (zoomKey === undefined) return;
    if (isFirstZoomKeyRef.current) {
      isFirstZoomKeyRef.current = false;
      return;
    }
    runProgrammaticZoomReset();
  }, [zoomKey, runProgrammaticZoomReset]);

  const handleResetZoom = useCallback(() => {
    runProgrammaticZoomReset();
    // Note: do NOT call onResetZoom here — that increments the shared zoomKey
    // which would reset ALL other charts. This button only resets this chart.
  }, [runProgrammaticZoomReset]);  // Memoize chart data processing for performance optimization
  const { chartData: processedChartData, flowBooleans } = useMemo(() => {
    let data: Array<{ x: number; y: number | null }> = [];
    let flowData: (number | null)[] = [];

    if (processedData.length > 0) {
      // Use actual data with timestamps
      data = processedData.map((point) => ({
        x: point.timestamp,
        y: point[dataType],
      }));
      flowData = processedData.map((point) => point.Flow);
    } else if (fromTimestamp && toTimestamp) {
      // Create empty data points for the time range
      const duration = toTimestamp - fromTimestamp;
      const numPoints = Math.min(
        10,
        Math.max(5, Math.floor(duration / (1000 * 60 * 60))),
      );

      for (let i = 0; i < numPoints; i++) {
        const timestamp = fromTimestamp + (i * duration) / (numPoints - 1);
        data.push({ x: timestamp, y: null });
        flowData.push(null);
      }
    } else {
      // Fallback empty data
      data = [{ x: Date.now(), y: null }];
      flowData = [null];
    }

    // Pre-compute boolean array for flow status — avoids per-segment function calls
    const flowBooleans = flowData.map((flow) => flow !== null && flow > 0);

    return { chartData: { data, flowData }, flowBooleans };
  }, [processedData, dataType, fromTimestamp, toTimestamp]);

  // Create datasets with conditional area fill and line segments based on flow (memoized)
  const isLargeDataset = processedChartData.data.length > 200;

  // Stable refs so the options memo doesn't need to depend on fast-changing arrays/callbacks.
  // This prevents Chart.js from re-initialising scales (and resetting zoom) on every data load.
  const flowBoolRef = useRef(flowBooleans);
  flowBoolRef.current = flowBooleans;
  const flowDataRef = useRef(processedChartData.flowData);
  flowDataRef.current = processedChartData.flowData;
  const onViewportChangeRef = useRef(onViewportChange);
  onViewportChangeRef.current = onViewportChange;

  const chartData = useMemo(
    () => ({
      datasets: [
        {
          label: title,
          data: processedChartData.data,
          borderColor: color,
          backgroundColor: `${color}4D`, // 30% opacity for area fill
          tension: 0.1,
          pointHoverRadius: 3,
          borderWidth: 2,
          fill: true,
          spanGaps: false, // Don't connect across gaps
          // Performance: hide individual points for large datasets
          pointRadius: isLargeDataset
            ? 0
            : flowBooleans.map((hasFlow) => (hasFlow ? 1.5 : 1)),
          pointHitRadius: 5,
          pointBackgroundColor: isLargeDataset
            ? color
            : flowBooleans.map((hasFlow) =>
                hasFlow ? color : "rgba(128, 128, 128, 0.5)",
              ),
          pointBorderColor: isLargeDataset
            ? "#fff"
            : flowBooleans.map((hasFlow) =>
                hasFlow ? "#fff" : "rgba(255, 255, 255, 0.3)",
              ),
          pointBorderWidth: isLargeDataset
            ? 0
            : flowBooleans.map((hasFlow) => (hasFlow ? 1.5 : 0.5)),
          // Skip Chart.js internal data parsing — data is already in {x,y} format
          parsing: false as any,
          // Data is sorted by timestamp — skip sort check
          normalized: true,
          // Use segment styling to conditionally show lines and area fill based on flow.
          // Uses ref to avoid re-creating the chart options on every data change.
          segment: {
            borderColor: (ctx: any) => {
              const bools = flowBoolRef.current;
              if (bools[ctx.p0DataIndex] && bools[ctx.p1DataIndex]) {
                return color;
              }
              return "transparent";
            },
            backgroundColor: (ctx: any) => {
              const bools = flowBoolRef.current;
              if (bools[ctx.p0DataIndex] && bools[ctx.p1DataIndex]) {
                return `${color}4D`;
              }
              return "transparent";
            },
            borderDash: (ctx: any) => {
              const bools = flowBoolRef.current;
              if (bools[ctx.p0DataIndex] !== bools[ctx.p1DataIndex]) {
                return [5, 5];
              }
              return undefined;
            },
          },
        },
      ],
    }),
    [processedChartData.data, flowBooleans, title, color, isLargeDataset],
  );

  // Imperatively update scale min/max when fromTimestamp/toTimestamp change but only if not zoomed
  React.useEffect(() => {
    if (chartRef.current && !isZoomed) {
      const chart = chartRef.current;
      chart.options.scales!.x!.min = fromTimestamp;
      chart.options.scales!.x!.max = toTimestamp;
      chart.update("none");
    }
  }, [fromTimestamp, toTimestamp, isZoomed]);

  const options: ChartOptions<"line"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      // Disable animations so zoom/pan is instant — avoids per-frame redraw lag
      animation: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        legend: {
          display: false,
        },
        decimation: {
          enabled: true,
          algorithm: "lttb",
          samples: 500,
        },
        zoom: {
          limits: zoomLimitsNoFuture(),
          pan: {
            enabled: true,
            mode: "x" as const,
            onPanStart: ({ chart }: any) => {
              refreshNoFutureLimit(chart);
              return true;
            },
            onPan: ({ chart }: any) => {
              refreshNoFutureLimit(chart);
            },
            onPanComplete: ({ chart }: any) => {
              if (suppressViewportRef.current) return;
              clampChartXToPresent(chart);
              if (!isZoomedRef.current) setIsZoomed(true);
              // Only after the user releases pan — avoids a request storm mid-drag
              onViewportChangeRef.current?.(chart.scales.x.min);
            },
          },
          zoom: {
            wheel: {
              enabled: true,
              speed: 0.1,
            },
            pinch: {
              enabled: true,
            },
            mode: "x" as const,
            onZoomStart: ({ chart }: any) => {
              refreshNoFutureLimit(chart);
              return true;
            },
            onZoom: ({ chart }: any) => {
              refreshNoFutureLimit(chart);
            },
            onZoomComplete: ({ chart }: any) => {
              if (suppressViewportRef.current) return;
              clampChartXToPresent(chart);
              if (!isZoomedRef.current) setIsZoomed(true);
              onViewportChangeRef.current?.(chart.scales.x.min);
            },
          },
        },
        tooltip: {
          enabled: true,
          mode: "index",
          intersect: false,
          backgroundColor: chartUi.tooltipBg,
          titleColor: chartUi.tooltipTitle,
          bodyColor: chartUi.tooltipBody,
          borderColor: chartUi.tooltipBorder,
          borderWidth: 1,
          callbacks: {
            title: function (tooltipItems) {
              const dataPoint = tooltipItems[0].raw as {
                x: number;
                y: number | null;
              };
              return format(new Date(dataPoint.x), "MMM dd, yyyy HH:mm");
            },
            label: function (context) {
              let label = title;
              if (label) label += ": ";
              if (context.parsed.y === null) return label;
              if (dataType === "Flow") {
                label += context.parsed.y > 0 ? "on" : "off";
                return label;
              }
              label += context.parsed.y.toFixed(2);
              if (unit) label += ` ${unit}`;
              return label;
            },
            afterLabel: function (context) {
              if (dataType === "Flow") return "";
              const flow = flowDataRef.current[context.dataIndex];
              if (flow === null) return "Flow: No data";
              return flow > 0 ? "Flow: on" : "Flow: off";
            },
          },
        },
      },
      scales: {
        x: {
          type: "time",
          time: {
            displayFormats: {
              hour: "HH:mm",
              day: "MMM dd",
              week: "MMM dd",
              month: "MMM yyyy",
            },
            tooltipFormat: "MMM dd, yyyy HH:mm",
          },
          min: fromTimestampRef.current,
          max: toTimestampRef.current,
          grid: {
            color: chartUi.grid,
          },
          ticks: {
            maxTicksLimit: 8,
            maxRotation: 45,
            minRotation: 0,
            color: chartUi.axis,
          },
        },
        y: {
          beginAtZero: false,
          grid: {
            color: chartUi.grid,
          },
          title: {
            display: !!unit,
            text: unit,
            color: chartUi.axis,
          },
          ticks: {
            color: chartUi.axis,
          },
          min: range.min,
          max: range.max,
          ...(customYAxis || {}),
        },
      },
    }),
    [title, unit, range, customYAxis, dataType, chartUi],
  );

  return (
    <ChartWrapper>
      <ChartHeader>
        <ChartTitle>{title}</ChartTitle>
      </ChartHeader>

      <ChartContainer>
        {isZoomed && (
          <ResetZoomButton onClick={handleResetZoom}>
            Reset Zoom
          </ResetZoomButton>
        )}
        <Line ref={chartRef} options={options} data={chartData} />
      </ChartContainer>
    </ChartWrapper>
  );
};
